import React, { useEffect, useState } from "react";
import ButtonOne from "../../common/normal-button";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Link } from "@mui/icons-material";
import {
  APPOINTMENT_MANAGEMENT_CHECK_GOOGLE_CALENDAR_SYNC_STATE,
  APPOINTMENT_MANAGEMENT_SYNC_GOOGLE_CALENDAR,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import typography from "../../cssUtils/typography";

const GoogleSync = () => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [syncState, setSyncState] = useState("loading");
  const [syncDisabled, setSyncDisabled] = useState(false);

  // Check sync state on mount
  const fetchGoogleSyncState = async () => {
    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(
        APPOINTMENT_MANAGEMENT_CHECK_GOOGLE_CALENDAR_SYNC_STATE,
        { headers },
      );

      const data = await res.json()

      if (data.disabled){
        setSyncDisabled(true);
      }

      if (data.connected) {
        setSyncState("synced");
      } else {
        setSyncState("not_synced");
      }
    } catch (err) {
      console.error("Failed to check Google Calendar sync state", err);
      setSyncState("not_synced");
    }
  };

  // Trigger sync / OAuth
  const syncGoogleCalendar = async () => {
    setSyncState("syncing");

    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(APPOINTMENT_MANAGEMENT_SYNC_GOOGLE_CALENDAR, {
        headers,
        method: "POST",
      });

      const data = await res.json()

      // Backend says already connected
      if (data.connected) {
        setSyncState("synced");
        return;
      }

      // Backend sends OAuth URL → redirect user
      if (data.auth_url) {
        window.location.href = data.auth_url;
        return;
      }

      // Fallback
      setSyncState("not_synced");
    } catch (err) {
      console.error("Failed to sync Google Calendar", err);
      setSyncState("not_synced");
    }
  };

  useEffect(() => {
    fetchGoogleSyncState();
  }, []);

  if (syncDisabled) {
    return (
      <Typography color="red" fontWeight="bold"  sx={{ ...typography.h1, color: "red"}}>
        Google Calendar Integration Disabled
      </Typography>
    );
  }

  // Initial loading
  if (syncState === "loading") {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <CircularProgress size={20} />
        <Typography  sx={{ ...typography.h1}}>Checking Google Calendar status...</Typography>
      </Stack>
    );
  }

  // Connected
  if (syncState === "synced") {
    return (
      <Typography color="green" sx={{ ...typography.h1, color: "green"}}>
        Google Calendar Connected
      </Typography>
    );
  }

  // Not connected
  if (syncState === "not_synced") {
    return (
      <ButtonOne
        onClick={syncGoogleCalendar}
        title="Connect Google Calendar"
        startIcon={<Link />}
      />
    );
  }

  // Syncing
  if (syncState === "syncing") {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <CircularProgress size={20} sx={{ ...typography.h1}} />
        <Typography fontWeight="bold">
          Connecting with Google Calendar…
        </Typography>
      </Stack>
    );
  }

  return null;
};

export default GoogleSync;
